import { cloneDeep } from 'lodash-es';
import { mapPageSortByForListApi } from '@/helpers/mapPageSortByForListApi';
import { ToolUserItem } from '@/models/toolUser';
import { ToolUserParamItem } from '@/models/toolUserParam';
import { createToolUsersPageHeaders } from '@/pages/admin/toolUsers/ToolUsersPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import ErrorService from '@/services/errorService';
import { toolUserParamsService } from '@/services/toolUserParamsService';
import { toolUserService } from '@/services/toolUserService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { IToolUserFilters, IToolUserMutationResponse } from '@/types/toolUser';
import type { IToolUserParamsDetailState } from '@/types/toolUserParams';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createToolUserStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<ToolUserItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const mailActionLoading = ref(false);
    const paramsDetailState = ref<Record<number, IToolUserParamsDetailState>>({});

    // --- filters ---
    const filters = reactive<IToolUserFilters>({
        userSearch: {
            value: null,
            title: 'toolUsers.filters.search',
            allowSaveState: false,
        },
        userId: {
            value: null,
            title: 'toolUsers.filters.userId',
            allowSaveState: false,
        },
        userTypeId: {
            value: null,
            title: 'toolUsers.filters.userTypeId',
            allowSaveState: true,
        },
        userOwnerId: {
            value: null,
            title: 'toolUsers.filters.userOwnerId',
            allowSaveState: true,
        },
        userStatus: {
            value: null,
            title: 'toolUsers.filters.userStatus',
            allowSaveState: true,
        },
        botStatus: {
            value: null,
            title: 'toolUsers.filters.botStatus',
            allowSaveState: true,
        },
        userCurrencyId: {
            value: null,
            title: 'toolUsers.filters.userCurrencyId',
            allowSaveState: true,
        },
        userPlatformId: {
            value: null,
            title: 'toolUsers.filters.userPlatformId',
            allowSaveState: true,
        },
        userObserveId: {
            value: null,
            title: 'toolUsers.filters.userObserveId',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.userSearch.value = null;
        filters.userId.value = null;
        filters.userTypeId.value = null;
        filters.userOwnerId.value = null;
        filters.userStatus.value = null;
        filters.botStatus.value = null;
        filters.userCurrencyId.value = null;
        filters.userPlatformId.value = null;
        filters.userObserveId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createToolUsersPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'toolUsers',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<IToolUserFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: mapPageSortByForListApi(options.value.sortBy, headers.value),
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Agent', 'ToolUserType', 'Currency', 'CurrencyAll', 'Country', 'AuditTable'],
                requestKey: `tool-users:${storeId}`,
            };

            const response = await toolUserService.list(params);
            items.value = response.data.rows.map((row) => new ToolUserItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function mergeMutationResponse(response: { data: IToolUserMutationResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;
        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new ToolUserItem(row);
        } else {
            items.value = [new ToolUserItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    async function patchBoolField(item: ToolUserItem, modelKey: 'isActive' | 'isSuspended' | 'isSuspicious', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await toolUserService.patch({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    async function sendActivationEmail(item: ToolUserItem) {
        mailActionLoading.value = true;
        try {
            const response = await toolUserService.sendActivationEmail({ rows: [{ id: item.id }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success !== false) {
                await loadData();
            }
        } catch {
            /* toolUserService wraps errors */
        } finally {
            mailActionLoading.value = false;
        }
    }

    async function sendVerifyEmail(item: ToolUserItem) {
        mailActionLoading.value = true;
        try {
            const response = await toolUserService.sendVerifyEmail({ rows: [{ id: item.id }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success !== false) {
                await loadData();
            }
        } catch {
            /* toolUserService wraps errors */
        } finally {
            mailActionLoading.value = false;
        }
    }

    function clearData() {
        items.value = [];
        paramsDetailState.value = {};
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
        mailActionLoading.value = false;
    }

    function getParamsDetailState(toolUserId: number): IToolUserParamsDetailState {
        paramsDetailState.value[toolUserId] ??= { loading: false, error: false, rows: [] };
        return paramsDetailState.value[toolUserId];
    }

    async function loadParams(entityId: number) {
        const state = getParamsDetailState(entityId);
        if (state.loading) return;

        state.loading = true;
        state.error = false;
        state.rows = [];

        try {
            const response = await toolUserParamsService.listByToolUserId(entityId);
            state.rows = response.data.rows.map((row) => new ToolUserParamItem(row));
        } catch (error) {
            state.error = true;
            ErrorService.log(error);
        } finally {
            state.loading = false;
        }
    }

    async function createParam(entityId: number, param: string, value: string): Promise<boolean> {
        try {
            const item = new ToolUserParamItem();
            item.toolUserId = entityId;
            item.param = param;
            item.value = value;

            const response = await toolUserParamsService.create({
                rows: [item.toCreateApiData()],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function updateParam(entityId: number, param: ToolUserParamItem, fields: Array<'param' | 'value'>): Promise<boolean> {
        try {
            const response = await toolUserParamsService.update({
                rows: [param.toUpdateApiData(fields)],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function deleteParam(entityId: number, paramId: number): Promise<boolean> {
        try {
            const response = await toolUserParamsService.delete({ rows: [{ id: paramId }] });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        mailActionLoading,
        options,
        loadData,
        updateOptions,
        patchBoolField,
        sendActivationEmail,
        sendVerifyEmail,
        mergeMutationResponse,
        clearData,
        getParamsDetailState,
        loadParams,
        createParam,
        updateParam,
        deleteParam,
        hasAdd: true,
    };
}

export function useToolUserStore(storeId: string = 'toolUsers') {
    const useStore = defineStore(storeId, () => createToolUserStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'toolUsers'),
    });
    return useStore();
}
